import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Send, User, Loader2, Play, X, FileText, Archive, Eye } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import BotDocumentReviewDialog from './BotDocumentReviewDialog';

export default function BotClarificationChatModal({ 
  open, 
  onOpenChange, 
  task,
  onExecutionComplete 
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [readyToExecute, setReadyToExecute] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [documentOutput, setDocumentOutput] = useState(null);
  const [showArtifactPrompt, setShowArtifactPrompt] = useState(false);
  const [artifactCategory, setArtifactCategory] = useState('other');
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ open: false, documentOutput: null });
  const scrollRef = useRef(null);

  // Analyze task when modal opens
  useEffect(() => {
    if (open && task) {
      analyzeTask();
    } else {
      // Reset state when modal closes
      setMessages([]);
      setInputValue('');
      setReadyToExecute(false);
      setAdditionalContext('');
      setDocumentOutput(null);
      setShowArtifactPrompt(false);
      setArtifactCategory('other');
    }
  }, [open, task?.id]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const analyzeTask = async () => {
    setIsLoading(true);
    try {
      const response = await base44.functions.invoke('executeBotTask', {
        task_id: task.id,
        mode: 'analyze'
      });

      const data = response.data;
      
      if (data.needs_clarification) {
        // Build message with questions
        let botMessage = data.message;
        if (data.questions && data.questions.length > 0) {
          botMessage += '\n\n' + data.questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
        }
        setMessages([{ role: 'bot', content: botMessage }]);
        setReadyToExecute(false);
      } else {
        setMessages([{
          role: 'bot',
          content: data.message || "I have all the information I need. Ready to execute this task!"
        }]);
        setReadyToExecute(true);
      }
    } catch (error) {
      setMessages([{
        role: 'bot',
        content: `Error analyzing task: ${error.response?.data?.error || error.message}. You can try executing the task directly.`
      }]);
      setReadyToExecute(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Accumulate context from user responses
    setAdditionalContext(prev => prev ? `${prev}\n\nUser clarification: ${userMessage}` : `User clarification: ${userMessage}`);
    
    // After user provides clarification, mark as ready to execute
    setMessages(prev => [...prev, {
      role: 'bot',
      content: "Got it! I've noted your clarification. Ready to execute when you are."
    }]);
    setReadyToExecute(true);
  };

  const handleExecute = async () => {
    setIsLoading(true);
    setMessages(prev => [...prev, {
      role: 'system',
      content: 'Executing task...'
    }]);

    try {
      const response = await base44.functions.invoke('executeBotTask', {
        task_id: task.id,
        additional_context: additionalContext || undefined
      });

      const data = response.data;
      
      if (data.document_output) {
        setDocumentOutput(data.document_output);
        setMessages(prev => [...prev, {
          role: 'bot',
          content: `✅ Task executed successfully!\n\nI created a document: **${data.document_output.suggested_file_name}**\n\nOpening it for your review...`
        }]);
        setIsLoading(false);
        // Open review dialog for user to approve/request changes
        setTimeout(() => {
          setReviewDialog({ open: true, documentOutput: data.document_output });
        }, 500);
      } else {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: '✅ Task executed successfully!'
        }]);
        setTimeout(() => {
          onOpenChange(false);
          if (onExecutionComplete) {
            onExecutionComplete(data);
          }
        }, 1500);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `Execution failed: ${error.response?.data?.error || error.message}`
      }]);
      setIsLoading(false);
    }
  };

  const handleSaveDocument = async (createArtifact) => {
    if (!documentOutput) return;
    setIsSavingDocument(true);
    
    try {
      const response = await base44.functions.invoke('saveBotDocument', {
        task_id: documentOutput.task_id,
        file_name: documentOutput.suggested_file_name,
        content: documentOutput.content,
        create_artifact: createArtifact,
        artifact_category: createArtifact ? artifactCategory : undefined
      });

      const data = response.data;
      setMessages(prev => [...prev, {
        role: 'bot',
        content: createArtifact 
          ? `📄 Document saved to Google Drive and Artifact created!\n\n[View Document](${data.drive_link})`
          : `📄 Document saved to Google Drive!\n\n[View Document](${data.drive_link})`
      }]);
      setShowArtifactPrompt(false);
      setDocumentOutput(null);
      
      setTimeout(() => {
        onOpenChange(false);
        if (onExecutionComplete) {
          onExecutionComplete({ success: true });
        }
      }, 2000);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `Failed to save document: ${error.response?.data?.error || error.message}`
      }]);
    } finally {
      setIsSavingDocument(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
    <BotDocumentReviewDialog
      open={reviewDialog.open}
      onOpenChange={(open) => {
        if (!open) {
          setReviewDialog({ open: false, documentOutput: null });
        }
      }}
      documentOutput={reviewDialog.documentOutput}
      taskId={task?.id}
      onApprove={(approvedDoc) => {
        setReviewDialog({ open: false, documentOutput: null });
        setDocumentOutput(approvedDoc);
        setMessages(prev => [...prev, {
          role: 'bot',
          content: `Document approved! Would you like to save it to Google Drive and optionally create an artifact?`
        }]);
        setShowArtifactPrompt(true);
      }}
      onCancel={() => {
        setReviewDialog({ open: false, documentOutput: null });
        setMessages(prev => [...prev, {
          role: 'bot',
          content: `Document discarded. Task is still marked as completed.`
        }]);
      }}
    />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bots-botclarificationchatmodal-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl h-[600px] flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-slate-800">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-400" />
            AI Officer Briefing
          </DialogTitle>
          {task && (
            <p className="text-sm text-slate-400 mt-1 line-clamp-2">
              Task: {task.task_description}
            </p>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-purple-400" />
                  </div>
                )}
                {msg.role === 'system' && (
                  <div className="w-8 h-8 rounded-full bg-slate-500/20 flex items-center justify-center flex-shrink-0">
                    <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                  </div>
                )}
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' 
                    ? 'bg-indigo-600 text-white' 
                    : msg.role === 'system'
                    ? 'bg-slate-700/50 text-slate-300 italic'
                    : 'bg-slate-800 text-slate-200'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'system' && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-purple-400" />
                </div>
                <div className="bg-slate-800 p-3 rounded-lg">
                  <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-slate-800 space-y-3">
          {showArtifactPrompt ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <FileText className="w-4 h-4 text-cyan-400" />
                <span>Save as artifact?</span>
              </div>
              <Select value={artifactCategory} onValueChange={setArtifactCategory}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Artifact category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="funnel">Funnel</SelectItem>
                  <SelectItem value="dashboard">Dashboard</SelectItem>
                  <SelectItem value="protocol">Protocol</SelectItem>
                  <SelectItem value="sop">SOP</SelectItem>
                  <SelectItem value="template">Template</SelectItem>
                  <SelectItem value="framework">Framework</SelectItem>
                  <SelectItem value="checklist">Checklist</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSaveDocument(false)}
                  disabled={isSavingDocument}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  {isSavingDocument ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Save to Drive Only
                </Button>
                <Button
                  onClick={() => handleSaveDocument(true)}
                  disabled={isSavingDocument}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                >
                  {isSavingDocument ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                  Save + Create Artifact
                </Button>
              </div>
            </div>
          ) : !readyToExecute ? (
            <div className="flex gap-2">
              <Textarea data-testid="bots-botclarificationchatmodal-textarea"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your response..."
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 min-h-[60px] max-h-[120px] resize-none"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-indigo-600 hover:bg-indigo-700 px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleExecute}
                disabled={isLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Execute Task
              </Button>
            </div>
          )}
          
          {!readyToExecute && !showArtifactPrompt && (
            <Button
              onClick={() => setReadyToExecute(true)}
              variant="ghost"
              className="w-full text-xs text-slate-500 hover:text-slate-300"
            >
              Skip clarification and execute directly
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}